import { Monster, Weapon, Reflector, EquipmentType, calculateSpecialAttackCount } from '../types';

/**
 * 装備プリセット（新仕様: 武器タイプA/B/C、リフレクタータイプA/B/C/D）
 */
export const EQUIPMENT_PRESETS: Record<
  EquipmentType,
  { weapon: Weapon; reflector: Reflector }
> = {
  A: {
    weapon: { name: 'タイプA武器', multiplier: 1.2 },
    reflector: { name: 'タイプAリフレクター', maxReflectCount: 2, reflectRate: 0.8 },
  },
  B: {
    weapon: { name: 'タイプB武器', multiplier: 1.15 },
    reflector: { name: 'タイプBリフレクター', maxReflectCount: 3, reflectRate: 0.7 },
  },
  C: {
    weapon: { name: 'タイプC武器', multiplier: 1.1 },
    reflector: { name: 'タイプCリフレクター', maxReflectCount: 7, reflectRate: 0.5 },
  },
  D: {
    weapon: { name: 'タイプD武器', multiplier: 1.0 },
    reflector: { name: 'タイプDリフレクター', maxReflectCount: 3, reflectRate: 1.0 },
  },
};

/**
 * 8魂格のモンスターデータベース（ストーリーモード初期値）
 */
export const MONSTER_DATABASE: Monster[] = [
  {
    id: 'zaag',
    name: 'レイン',
    species: '衡騎士',
    stats: { hp: 204, strength: 124, special: 124, speed: 124, toughness: 124, specialAttackCount: calculateSpecialAttackCount(124) },
    weapon: { name: '幻銀の剣', multiplier: EQUIPMENT_PRESETS.B.weapon.multiplier },
    reflector: {
      name: '幻銀の盾',
      maxReflectCount: EQUIPMENT_PRESETS.A.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.A.reflector.reflectRate,
    },
  },
  {
    id: 'gardan',
    name: 'ドルグ',
    species: '巌巨人',
    stats: {
      hp: 223,
      strength: 178,
      special: 73,
      speed: 57,
      toughness: 140,
      specialAttackCount: calculateSpecialAttackCount(73),
    },
    weapon: { name: '岩砕の拳', multiplier: EQUIPMENT_PRESETS.A.weapon.multiplier },
    reflector: {
      name: '岩壁の護り',
      maxReflectCount: EQUIPMENT_PRESETS.B.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.B.reflector.reflectRate,
    },
  },
  {
    id: 'roona',
    name: 'シエル',
    species: '星詠霊',
    stats: {
      hp: 172,
      strength: 89,
      special: 178,
      speed: 151,
      toughness: 86,
      specialAttackCount: calculateSpecialAttackCount(178),
    },
    weapon: { name: '星霊の杖', multiplier: EQUIPMENT_PRESETS.C.weapon.multiplier },
    reflector: {
      name: '星霊の障壁',
      maxReflectCount: EQUIPMENT_PRESETS.B.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.B.reflector.reflectRate,
    },
  },
  {
    id: 'zephyr',
    name: 'カイ',
    species: '天翔',
    stats: {
      hp: 204,
      strength: 108,
      special: 108,
      speed: 190,
      toughness: 108,
      specialAttackCount: calculateSpecialAttackCount(108),
    },
    weapon: { name: '疾風の爪', multiplier: EQUIPMENT_PRESETS.B.weapon.multiplier },
    reflector: {
      name: '疾風の羽',
      maxReflectCount: EQUIPMENT_PRESETS.C.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.C.reflector.reflectRate,
    },
  },
  {
    id: 'balga',
    name: 'ゲンブ',
    species: '金剛衛',
    stats: {
      hp: 194,
      strength: 113,
      special: 108,
      speed: 49,
      toughness: 178,
      specialAttackCount: calculateSpecialAttackCount(108),
    },
    weapon: { name: '玄武の牙', multiplier: EQUIPMENT_PRESETS.B.weapon.multiplier },
    reflector: {
      name: '玄武の甲羅',
      maxReflectCount: EQUIPMENT_PRESETS.D.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.D.reflector.reflectRate,
    },
  },
  {
    id: 'morsu',
    name: 'ユグド',
    species: '太古翁',
    stats: {
      hp: 260,
      strength: 108,
      special: 124,
      speed: 86,
      toughness: 105,
      specialAttackCount: calculateSpecialAttackCount(124),
    },
    weapon: { name: '古樹の腕', multiplier: EQUIPMENT_PRESETS.B.weapon.multiplier },
    reflector: {
      name: '古樹の根',
      maxReflectCount: EQUIPMENT_PRESETS.B.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.B.reflector.reflectRate,
    },
  },
  {
    id: 'graon',
    name: 'ボルグ',
    species: '戦修羅',
    stats: {
      hp: 229,
      strength: 143,
      special: 67,
      speed: 89,
      toughness: 156,
      specialAttackCount: calculateSpecialAttackCount(67),
    },
    weapon: { name: '蛮族の戦斧', multiplier: EQUIPMENT_PRESETS.A.weapon.multiplier },
    reflector: {
      name: '蛮族の護符',
      maxReflectCount: EQUIPMENT_PRESETS.C.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.C.reflector.reflectRate,
    },
  },
  {
    id: 'igna',
    name: 'アーシュ',
    species: '紅蓮魔',
    stats: {
      hp: 153,
      strength: 143,
      special: 163,
      speed: 144,
      toughness: 79,
      specialAttackCount: calculateSpecialAttackCount(163),
    },
    weapon: { name: '焔の爪', multiplier: EQUIPMENT_PRESETS.C.weapon.multiplier },
    reflector: {
      name: '焔の羽',
      maxReflectCount: EQUIPMENT_PRESETS.B.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.B.reflector.reflectRate,
    },
  },
  {
    id: 'alice',
    name: 'アリス',
    species: '傀儡少女',
    stats: {
      hp: 276,
      strength: 94,
      special: 164,
      speed: 52,
      toughness: 44,
      specialAttackCount: calculateSpecialAttackCount(164),
    },
    weapon: { name: '呪縛の鎌', multiplier: EQUIPMENT_PRESETS.C.weapon.multiplier },
    reflector: {
      name: '禁忌の鏡',
      maxReflectCount: EQUIPMENT_PRESETS.C.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.C.reflector.reflectRate,
    },
  },
  {
    id: 'mococo',
    name: 'モココ',
    species: '夢幻遊戯',
    stats: {
      hp: 202,
      strength: 176,
      special: 44,
      speed: 176,
      toughness: 48,
      specialAttackCount: calculateSpecialAttackCount(44),
    },
    weapon: { name: 'もふもふの拳', multiplier: EQUIPMENT_PRESETS.A.weapon.multiplier },
    reflector: {
      name: '異界の加護',
      maxReflectCount: EQUIPMENT_PRESETS.D.reflector.maxReflectCount,
      reflectRate: EQUIPMENT_PRESETS.D.reflector.reflectRate,
    },
  },
];

/**
 * 能力UP値の型
 */
interface AbilityUpValue {
  hp: number;
  strength: number;
  special: number;
  speed: number;
  toughness: number;
}

/**
 * 種族別の1段階あたり能力UP値（最大7段階）
 * 初期値 + 成長×7 = 対戦モード最終パラメータ
 */
export const ABILITY_UP_VALUES: Record<string, AbilityUpValue> = {
  zaag: { hp: 28, strength: 18, special: 18, speed: 18, toughness: 18 },
  gardan: { hp: 31, strength: 26, special: 11, speed: 9, toughness: 20 },
  roona: { hp: 24, strength: 13, special: 26, speed: 22, toughness: 12 },
  zephyr: { hp: 28, strength: 16, special: 16, speed: 25, toughness: 16 },
  balga: { hp: 28, strength: 16, special: 16, speed: 8, toughness: 26 },
  morsu: { hp: 40, strength: 16, special: 18, speed: 12, toughness: 15 },
  graon: { hp: 33, strength: 21, special: 9, speed: 13, toughness: 22 },
  igna: { hp: 21, strength: 21, special: 21, speed: 18, toughness: 13 },
  alice: { hp: 32, strength: 13, special: 28, speed: 14, toughness: 8 },
  mococo: { hp: 24, strength: 37, special: 8, speed: 37, toughness: 11 },
};

/** 隠しキャラクターのID一覧（解放条件を満たすまでロック） */
export const HIDDEN_MONSTER_IDS: readonly string[] = ['alice', 'mococo'] as const;

/** 通常キャラクター（隠しキャラを除く）のID一覧 */
export const NORMAL_MONSTER_IDS: string[] = MONSTER_DATABASE
  .map((m) => m.id)
  .filter((id) => !HIDDEN_MONSTER_IDS.includes(id));

/**
 * IDでモンスターを取得
 */
export function getMonsterById(id: string): Monster | undefined {
  return MONSTER_DATABASE.find((m) => m.id === id);
}

/**
 * モンスター詳細情報の型
 */
export interface MonsterDetailData {
  /** 魂格の説明文 */
  speciesDescription: string;
  /** 得意距離 */
  preferredRange: '近距離' | '遠距離' | '中距離' | '万能';
  /** 推奨スタンス */
  recommendedStance: '攻勢' | '守勢' | '通常' | '状況による';
  /** 戦法ヒント（一行テキスト） */
  battleHint: string;
}

/**
 * モンスターIDをキーとした詳細情報
 */
export const MONSTER_DETAIL_DATA: Record<string, MonsterDetailData> = {
  zaag: {
    speciesDescription: '万能の戦闘技を究めた騎士型の魂格。特定の突出した能力はないが、すべてのパラメータが高水準に整っており、どんな戦況にも柔軟に対応できる。初めて導き手と契約する魂格としても知られる。',
    preferredRange: '万能',
    recommendedStance: '状況による',
    battleHint: '万能。状況に応じて戦法を切り替えろ',
  },
  gardan: {
    speciesDescription: '大地の岩が意思を持ち、巨人として結晶化した魂格。素早さは低く鈍重だが、その腕力は全魂格中トップクラス。近距離での一撃は相手を粉砕するほどの威力を誇る。動きは遅くとも、当たれば決着がつく。',
    preferredRange: '近距離',
    recommendedStance: '攻勢',
    battleHint: '近距離で武器ぶっぱ。当たれば致命的',
  },
  roona: {
    speciesDescription: '星の魔力から生まれた浮遊する精霊型の魂格。物理的な強さは乏しく耐久も低いが、特殊攻撃の威力と手数が全魂格中最高峰。遠距離から7回の連続魔法攻撃で相手を圧倒するスタイルを得意とする。',
    preferredRange: '遠距離',
    recommendedStance: '攻勢',
    battleHint: '遠距離から特殊連打。回数7回と多い',
  },
  zephyr: {
    speciesDescription: '風を纏い天を翔る飛行型の魂格。素早さが全魂格中最大で、相手の攻撃を軽やかに回避しながら戦う。攻守いずれのパラメータも平均以上に整っており、リフレクターの回数も多い。素早さを活かした立ち回りが鍵。',
    preferredRange: '万能',
    recommendedStance: '守勢',
    battleHint: '素早さ最大。回避を活かしてリフレクターも駆使せよ',
  },
  balga: {
    speciesDescription: '金剛の如き硬さを誇る守護型の魂格。丈夫さが全魂格中トップクラスで、相手の攻撃を受け流す耐久戦が得意。その代わり素早さは全魂格中最低で動きは鈍い。リフレクターは完全反射タイプで、特殊攻撃を丸ごと跳ね返す。',
    preferredRange: '万能',
    recommendedStance: '守勢',
    battleHint: '丈夫さ最大＋完全反射リフレクター。長期戦に持ち込め',
  },
  morsu: {
    speciesDescription: '太古の昔から永い時を生きる長寿型の魂格。HPが全魂格中最大で、圧倒的な生命力で相手の攻撃を耐え続ける。攻撃力は平均的だが、高いHPを活かして粘り続けることで真価を発揮する。',
    preferredRange: '万能',
    recommendedStance: '守勢',
    battleHint: '高いHPで粘り勝つ。生き残ることを最優先に',
  },
  graon: {
    speciesDescription: '戦闘狂と化した修羅型の魂格。腕力と丈夫さが高く、近距離での物理戦闘に特化している。特殊攻撃はほぼ使えないが、武器攻撃の火力は高く、近距離に張り付いて圧力をかけ続けるのが基本戦術。',
    preferredRange: '近距離',
    recommendedStance: '攻勢',
    battleHint: '近距離に張り付いて殴る。特殊は捨て',
  },
  igna: {
    speciesDescription: '紅蓮の炎を操る魔型の魂格。腕力・特殊・素早さの三つが高水準に揃った攻撃特化型だが、HPと丈夫さは全魂格中最低クラスの紙装甲。一撃でも多くのダメージを叩き込む前に自分が倒れる、ハイリスクハイリターンな魂格。',
    preferredRange: '遠距離',
    recommendedStance: '攻勢',
    battleHint: '腕力・特殊・素早さ全部高い。紙装甲なので先手必勝',
  },
  erebos: {
    speciesDescription: '封印されていた禁忌の第9魂格。8魂格の負の側面を集約した「第9の影」。全パラメータが既存魂格の最高値に匹敵し、特殊攻撃への耐性を持つ。第1形態は神々しい天使の姿を纏うが、HPが尽きると真の姿——混沌の渦——へと変貌する。',
    preferredRange: '万能',
    recommendedStance: '状況による',
    battleHint: '特殊耐性あり。物理も混ぜて3体で役割分担して挑め',
  },
  erebos_form2: {
    speciesDescription: '封印が解けたエレボスの真の姿。8魂格の混沌が渦を巻き、全てを飲み込む暗黒の怪物と化している。素早さは低いが、圧倒的な腕力・特殊・丈夫さを誇る。反射率1.5倍の増幅リフレクターは要警戒。',
    preferredRange: '万能',
    recommendedStance: '状況による',
    battleHint: '増幅リフレクターに注意。特殊より物理が通りやすい',
  },
  alice: {
    speciesDescription: '導き手の知らぬ地で生まれた呪物型の魂格。人の手で作られた球体関節の人形に、禁忌の術で魂が封じられている。物理的な耐久は全魂格中最弱だが、HPと特殊攻撃の総量は並の魂格を凌駕し、遠距離から魔術を撃ち続けながら反射で耐える戦法を得意とする。誰が何のために造ったのかは定かではない。',
    preferredRange: '遠距離',
    recommendedStance: '守勢',
    battleHint: '紙装甲だがHP最大級。遠距離で特殊連打と反射で耐えろ',
  },
  mococo: {
    speciesDescription: '異界から迷い込んだとされる球状の着ぐるみ型の魂格。柔らかな布地の体と巨大な瞳、短い手足を持つ、どこまでも愛らしい外見のマスコット。しかしその可愛らしさに反して全魂格中最高の腕力と素早さを兼ね備え、一瞬で距離を詰めては豪腕を振るう。紙同然の丈夫さが唯一の弱点。中身が何なのかは誰も知らない。',
    preferredRange: '近距離',
    recommendedStance: '攻勢',
    battleHint: '超速で接近、最強火力で瞬殺。被弾即死',
  },
};

/**
 * IDでモンスター詳細情報を取得
 */
export function getMonsterDetailData(id: string): MonsterDetailData | undefined {
  return MONSTER_DETAIL_DATA[id];
}
